// there are a lot of URLs and keys in this file
/* eslint-disable max-len */
import { GymEnvironment } from 'fitbeat.models';
import { Platform } from 'react-native';
import { AudioProfileType, AudioScenarioType } from 'react-native-agora';
import { variant } from './build';

export const DEFAULT_ACCENT_COLOR = 'FF621D';

const defaultConfig = {
  apiBaseUrl: 'http://localhost:8080',
  hrMetricsServer: 'http://localhost:7080',
  // do we still need this? it defaults to a home gym for staging and old studio for prod
  defaultGym: 'SydneySussex',
  publicPageBaseUrl: 'https://fitbeat.com',
  webAppBaseUrl: 'https://app.fitillion.com',
  stripePublishableKey: 'pk_test_51HH8LdAcaWctOtVHK1kvAEbqgkqBXHFjSC47FZAgDkoZWzlrlihSTYWOuCXhVF7QzadWGHVs9AN0UCKd670huTfH00bTW3Ugby',
  debug: (typeof __DEV__ !== 'undefined' && __DEV__),
  variant, // todo: deprecate, replace with build.variant
  auth: {
    useWebBreakoutForGoogleAuth: variant.isElite,
    facebookEnabled: true,
  },
  workoutRenderingEnabled: true,
  workoutUpdateActionEnabled: true,
  workoutUpdateReducerEnabled: true,
  debugWorkoutInfoVisible: false,
  showWorkoutVideo: true,
  workoutTickDelayOverride: undefined,
  allowBluetoothService: true,
  isOrientationChecksEnabled: true,
  renderGenericMusic: true,
  delayWhenLoadingWorkoutVideo: 150,
  playWorkoutAudioTips: true,
  enableWorkoutLeaderboardUpdates: true,
  useNativeLoggerForDebugLogs: false,
  autoStartWorkout: false,
  autoEndWorkout: false,
  profileHomeWorkoutRender: false,
  useDebugLogger: true,
  agoraStreamingAudioProfile: AudioProfileType.AudioProfileDefault,
  agoraStreamingAudioScenario: AudioScenarioType.AudioScenarioChorus,
  environmentName: 'coggy',
  iapHubAppId: '61a4d7880235915d05e998ee',
  iapHubApiKey: 'jGJMnffOaSt6imdZKcGWXdz7xfmMyN',
  spotifyClientId: 'a23ab719f4744155bc3c1c236abcbaea',
  spotifyRedirectURL: 'fitbeat://spotify-login-callback',
  friendInviteEnabledForStudio: true,
  genericMusicDuckedVolumePercentage: 0.3,
  streamingWorkoutVolume: Platform.OS === 'ios' ? 0.1 : 0.2, // this is generic music unducked
  streamingWorkoutDuckedVolume: 0.1, // generic music ducked
  streamingWorkoutBeepAndVoiceVolume: Platform.OS === 'ios' ? 30 : 40,
  streamingWorkoutMusicDelayInMs: 6000, // gives the video call time to start up
  groupWorkoutVideoCallStartDelayInMs: 1000, // allows time for video loading before the call, to avoid issues
  nutritionPlaceHolderImage: 'https://res.cloudinary.com/fitbeat-operations/image/upload/t_MealDesktopWide3/Meals/300-desktop.jpg',
  nutritionDashTileImageType: 'urlForDesktopImage',
};

function buildConfig(environmentSpecificValues) {
  // build into a new object, from default config, and overwrite with environmentSpecificValues
  const result = { ...defaultConfig, ...environmentSpecificValues };

  function buildPublicPagesUrls(baseUrl, webAppBaseUrl, apiBaseUrl) {
    return {
      allTermsAndCondition: `${baseUrl}/all-terms-and-conditions/?minView&accent-color=${DEFAULT_ACCENT_COLOR}`,
      faq: `https://fitillion.com/help-mobile?accent-color=${DEFAULT_ACCENT_COLOR}`,
      desktopFaq: `https://fitillion.com/help?accent-color=${DEFAULT_ACCENT_COLOR}`,
      exerciseGuide: `${baseUrl}/exercise-guide?accent-color=${DEFAULT_ACCENT_COLOR}`,
      membershipPlans: `${apiBaseUrl}/membership-plans`,
      gymWebPage: `${apiBaseUrl}/gym-webpage`,
      calendarBooking: `${apiBaseUrl}/calendar`,
      nutritionScience: `${apiBaseUrl}/nutrition-science`,
      shopGearUrl: `${apiBaseUrl}/shop-gear`,
      screenCastingFaqIos: `${baseUrl}/casting-faqios/what-device-do-you-want-to-connect-to?accent-color=${DEFAULT_ACCENT_COLOR}`,
      screenCastingFaqAndroid: `${baseUrl}/casting-faqand/what-device-do-you-want-to-connect-to?accent-color=${DEFAULT_ACCENT_COLOR}`,
      heartRateFaq: `${baseUrl}/heart-faqios/what-type-of-heart-rate-monitor-do-you-want-to-connect?accent-color=${DEFAULT_ACCENT_COLOR}`,
      studioHRMInstructions: `https://studios.fitillion.com/heart-faq-studio/options?accent-color=${DEFAULT_ACCENT_COLOR}`,
      appleMusicAppStore: 'itms://itunes.apple.com/app/music/id1108187390?mt=8',
      appleMusicPlayStore: 'market://details?id=com.apple.android.music',
      spotifyAppStore: 'itms://itunes.apple.com/app/spotify-music/id324684580?mt=8',
      spotifyPlayStore: 'market://details?id=com.spotify.music',
      learnMoreAboutOurSciencFaq: `https://fitillion.com/help/progress-and-fitness-tracking/learn-more-about-our-target-tracking-science/?accent-color=${DEFAULT_ACCENT_COLOR}`,
      scaleScienceFaq: `https://fitillion.com/help/progress-and-fitness-tracking/learn-more-about-our-science/?accent-color=${DEFAULT_ACCENT_COLOR}`,
      learnHowToReachTargetFaq: `https://fitillion.com/help/progress-and-fitness-tracking/how-do-i-track-my-fitness-and-workout-progress/?accent-color=${DEFAULT_ACCENT_COLOR}`,
      learnHowToReachTargetLeanAndFitFaq: `https://fitillion.com/help/progress-and-fitness-tracking/how-do-i-track-my-fitness-and-workout-progress/lean-fit/?accent-color=${DEFAULT_ACCENT_COLOR}`,
      learnHowToReachTargetAllRounderFaq: `https://fitillion.com/help/progress-and-fitness-tracking/how-do-i-track-my-fitness-and-workout-progress/all-rounder/?accent-color=${DEFAULT_ACCENT_COLOR}`,
      learnHowToReachTargetMuscleAndStrengthFaq: `https://fitillion.com/help/progress-and-fitness-tracking/how-do-i-track-my-fitness-and-workout-progress/muscle-strength/?accent-color=${DEFAULT_ACCENT_COLOR}`,
      connectTrackingDeviceAndroidFaq: `https://studios.fitillion.com/help/progress-and-fitness-tracking/connecting-your-smart-watch-or-fitness-tracker/connect-tracking-device-or-smart-watch-on-android/?accent-color=${DEFAULT_ACCENT_COLOR}`,
      connectTrackingDeviceIosFaq: `https://studios.fitillion.com/help/progress-and-fitness-tracking/connecting-your-smart-watch-or-fitness-tracker/connect-tracking-device-or-smart-watch-on-iphone/?accent-color=${DEFAULT_ACCENT_COLOR}`,
      goalOverviewNutritionAndWorkout: `https://fitillion.com/help/getting-started/goal-overview-nutrition-and-workouts/?accent-color=${DEFAULT_ACCENT_COLOR}`,
      googleAuthWebBreakoutUrl: `${webAppBaseUrl}/_auth/google-login.html`,
      appleSignInReturnUrl: 'https://bold-azimuth-196305.firebaseapp.com/__/auth/handler',
      websiteStudio: 'https://fitillion.com/studio',
      equipmentSetup: 'https://studios.fitillion.com/setup-equipment/',
    };
  }

  result.pages = buildPublicPagesUrls(result.publicPageBaseUrl, result.webAppBaseUrl, result.apiBaseUrl);
  result.isProductionEnvironment = result.environmentName === 'prod';
  result.gymEnvironment = result.isProductionEnvironment
    ? GymEnvironment.Production
    : GymEnvironment.Staging;
  return result;
}

// todo: not a nice check for environment type on web,
// should maybe set the config that is used instead, or create web-specific config nested in environmentSetConfig
const isWebAndNotProd = Platform.OS === 'web' && process.env.ENVIRONMENT_NAME !== 'prod';
const webOverride = {
  auth: { ...defaultConfig.auth, facebookEnabled: true },
};

export const environmentSetConfig = buildConfig({
  displayName: `Default (${process.env.ENVIRONMENT_NAME || 'CI-set Environment'})`,
  apiBaseUrl: process.env.API_BASE_URL || defaultConfig.apiBaseUrl,
  hrMetricsServer: process.env.HR_METRICS_SERVER || defaultConfig.hrMetricsServer,
  publicPageBaseUrl: process.env.LANDING_BASE_URL || defaultConfig.publicPageBaseUrl,
  webAppBaseUrl: defaultConfig.webAppBaseUrl,
  defaultGym: process.env.GYM_NAME || defaultConfig.defaultGym,
  stripePublishableKey: process.env.STRIPE_PUBLISHABLE_KEY || defaultConfig.stripePublishableKey,
  appDownloadUrl: process.env.APP_DOWNLOAD_URL,
  environmentName: process.env.ENVIRONMENT_NAME || defaultConfig.environmentName,
  ...(isWebAndNotProd ? webOverride : undefined),
});

export const stagingConfig = buildConfig({
  displayName: 'Staging',
  apiBaseUrl: 'https://api-staging.fitillion.com',
  hrMetricsServer: 'https://hr-metrics-staging.fitillion.com',
  webAppBaseUrl: 'https://app-staging.fitillion.com',
  defaultGym: 'HomeSA',
  environmentName: 'coggy',
});

export const devConfig = buildConfig({
  displayName: 'local development',
  apiBaseUrl: 'http://localhost:8080',
  hrMetricsServer: 'http://localhost:7080',
  webAppBaseUrl: 'http://localhost:8000',
  defaultGym: 'HomeSA',
  debug: true,
  environmentName: 'coggy',
});
